import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useStore } from 'effector-react';
import { closeModal } from '@stores/modals';
import { ALL_LOCALE, LOCALE_NAME } from '@stores/localeWeight';
import { Button, DatePicker, Form, Input, Modal, Radio, Select, Spin } from 'antd';
import useTranslate from '@hooks/useTranslate';
import { useForm } from 'antd/lib/form/Form';
import componentContent from '../GamesPromotionPage.content.json';
import { FULL_TIME_FORMAT, TIME_FORMAT } from '@config/format';
import DebounceSelect from '@fields/DebounceSelect/DebounceSelect';
import css from './GamePromotionEditModal.module.less';
import { Option } from 'antd/lib/mentions';
import { IPromotionGames, IPromotionGamesAction } from '@interfaces/IPromotionGames';
import {
  fetchGameList,
  mapDataToFieldsValue,
  mapValueToRequest,
  mapValueToRequestOnCreate,
  rulesValidateForm,
} from './GamePromotionEditModal.helpers';
import { createPromotion, editPromotion, getList } from '@stores/promotion';
import { PromotionFormValue } from './GamePromotionEditModal.types';
import { ALL_RESOURCE, RESOURCE_NAME } from '@constants/resource';
import { ExclamationCircleFilled } from '@ant-design/icons';

const { confirm } = Modal;
const LOCALES = ALL_LOCALE.map((locale) => ({
  label: LOCALE_NAME[locale],
  value: locale,
}));

const RESOURCE = ALL_RESOURCE.map((resource) => ({
  label: RESOURCE_NAME[resource],
  value: resource,
}));

const GamePromotionEditModal: FunctionComponent<{ data?: IPromotionGames }> = ({ data }) => {
  const [isHover, setIsHover] = useState(false);
  const [isError, setIsError] = useState<boolean>(false);
  const handleMouseEnter = () => {
    setIsHover(true);
  };

  const handleMouseLeave = () => {
    setIsHover(false);
  };

  const content = useTranslate(componentContent);
  const [form] = useForm();
  const locales = useMemo(() => LOCALES, []);

  useEffect(() => {
    if (data) {
      const fieldsValue = mapDataToFieldsValue(data);
      form.setFieldsValue(fieldsValue);
    } else {
      form.resetFields();
    }
  }, [data, form]);

  const onClose = (
    description: string,
    title: string,
    no: string,
    yes: string,
    onConfirm?: any,
  ) => {
    confirm({
      title: title,
      icon: <ExclamationCircleFilled />,
      content: description,
      okText: yes,
      cancelText: no,
      onOk() {
        onConfirm();
      },
      onCancel() {
        closeModal({ name: 'promotion' });
      },
    });
  };

  const onFinish = (values: PromotionFormValue) => {
    if (data?.id) {
      const dataForRequest: IPromotionGamesAction = mapValueToRequest(values);
      editPromotion({
        ...dataForRequest,
        id: data.id,
      })
        .then(() => {
          setIsError(false);
          closeModal({ name: 'promotion' });
        })
        .catch(() => {
          setIsError(true);
        });
    } else {
      const dataForRequest: IPromotionGamesAction = mapValueToRequestOnCreate(values);
      createPromotion(dataForRequest)
        .then(() => {
          setIsError(false);
          closeModal({ name: 'promotion' });
        })
        .catch(() => {
          setIsError(true);
          form.resetFields();
        });
    }
  };

  return (
    <Modal
      title={data ? content.edit_promo : content.add_promo}
      visible
      onCancel={() =>
        onClose(
          content.confirm_description,
          content.confirm_title,
          content.no,
          content.yes,
          form.submit,
        )
      }
      width={800}
      footer={[
        <Button
          key='cancel'
          type='primary'
          danger
          onClick={() =>
            onClose(
              content.confirm_description,
              content.confirm_title,
              content.no,
              content.yes,
              form.submit,
            )
          }
        >
          {content.cancel}
        </Button>,
        <Button
          key='submit'
          htmlType='submit'
          type='primary'
          className={css.submitButton}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={() =>
            onClose(
              content.confirm_description,
              content.confirm_title,
              content.no,
              content.yes,
              form.submit,
            )
          }
        >
          {content.save}
        </Button>,
      ]}
    >
      <Form
        onFinish={onFinish}
        form={form}
        labelAlign='left'
        labelWrap
        labelCol={{ flex: '150px' }}
      >
        <Spin spinning={false}>
          <Form.Item
            label={content.promotion_name}
            name='name'
            validateTrigger='onBlur'
            rules={[rulesValidateForm.name.base]}
          >
            <Input type={'text'} />
          </Form.Item>

          <Form.Item
            label={content.promotion_game}
            name='gameId'
            validateTrigger='onBlur'
            rules={[rulesValidateForm.gameId.base]}
          >
            <DebounceSelect
              mode='tags'
              placeholder={content.select_games}
              fetchOptions={fetchGameList}
            />
          </Form.Item>

          <Form.Item
            label={content.display_period}
            name='date'
            validateTrigger='onBlur'
            rules={[rulesValidateForm.date.base]}
          >
            <DatePicker.RangePicker
              showTime={{ format: TIME_FORMAT }}
              format={FULL_TIME_FORMAT}
            />
          </Form.Item>

          <Form.Item
            label={content.position}
            name='position'
            validateTrigger='onBlur'
            rules={[rulesValidateForm.position.base]}
          >
            <Input type={'number'} />
          </Form.Item>

          <Form.Item
            label={content.resource}
            name='searchSource'
            rules={[rulesValidateForm.position.base]}
          >
            <Select
              style={{ width: 240 }}
              defaultValue={content.resource}
            >
              {RESOURCE &&
                Object.entries(RESOURCE).map(([resource, { value, label }]) => {
                  return (
                    <Option
                      value={value}
                      key={resource}
                    >
                      {label}
                    </Option>
                  );
                })}
            </Select>
          </Form.Item>

          <Form.Item
            label={content.locales}
            name='locale'
          >
            <Radio.Group>
              {locales.map((locale) => (
                <Radio
                  value={locale.value}
                  key={locale.value}
                >
                  {locale.label}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        </Spin>
        {isError && <span className='ant-form-item-explain-error'>{content.form_error}</span>}
      </Form>
    </Modal>
  );
};

export default GamePromotionEditModal;
